/**
 * Admin Alerts Service
 * SMS / email alert rules sent to admins when something needs attention
 */

import { buildApiUrl } from '@/utils/apiHelpers';

export type AlertCategory = 'reviews' | 'money' | 'bookings' | 'accounts' | 'system';
export type AdminRole = 'super_admin' | 'admin' | 'manager' | 'staff' | 'support';

export interface AlertType {
  key: string;
  name: string;
  description: string;
  category: AlertCategory;
  variables: string[];
  defaultSubject: string;
  defaultEmail: string;
  defaultSms: string;
}

export interface AlertAdmin {
  id: string;
  name: string;
  email: string;
  role: string;
  hasPhone: boolean;
}

export interface AlertRule {
  id: string;
  alertType: string;
  alertTypeName?: string;
  name: string;
  enabled: boolean;
  sendEmail: boolean;
  sendSms: boolean;
  recipientRoles: string[];
  recipientAdminIds: string[];
  extraEmails: string[];
  extraPhones: string[];
  subjectTemplate: string | null;
  emailTemplate: string | null;
  smsTemplate: string | null;
  usesDefaultWording?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export type AlertRuleInput = Omit<AlertRule, 'id' | 'alertTypeName' | 'usesDefaultWording' | 'createdAt' | 'updatedAt'>;

export interface AlertTestResult {
  sent: number;
  failed: number;
  skipped: number;
  message: string;
}

export interface AlertLogEntry {
  id: string;
  ruleId: string | null;
  ruleName: string | null;
  alertType: string;
  alertTypeName: string | null;
  channel: 'email' | 'sms';
  recipient: string;
  status: 'sent' | 'failed';
  error: string | null;
  sentAt: string;
}

export class AdminAlertsError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const response = await fetch(buildApiUrl(`/admin/alerts${path}`, 'v1'), {
    ...init,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });

  const body = await response.json().catch(() => ({}));

  if (!response.ok || body.success === false) {
    const err = body?.error;
    const message = (typeof err === 'string' ? err : err?.message) || body?.message || `Request failed (${response.status})`;
    throw new AdminAlertsError(message, response.status, typeof err === 'object' ? err?.code : undefined);
  }

  return body.data as T;
};

export const adminAlertsService = {
  getTypes: () => request<{ types: AlertType[]; roles: AdminRole[] }>('/types'),

  getAdmins: () => request<AlertAdmin[]>('/admins'),

  getRules: () => request<AlertRule[]>('/rules'),

  createRule: (rule: AlertRuleInput) =>
    request<AlertRule>('/rules', { method: 'POST', body: JSON.stringify(rule) }),

  updateRule: (id: string, changes: Partial<AlertRuleInput>) =>
    request<AlertRule>(`/rules/${id}`, { method: 'PUT', body: JSON.stringify(changes) }),

  deleteRule: (id: string) => request<unknown>(`/rules/${id}`, { method: 'DELETE' }),

  testRule: (id: string, to: 'me' | 'recipients') =>
    request<AlertTestResult>(`/rules/${id}/test`, { method: 'POST', body: JSON.stringify({ to }) }),

  getLog: (filters: { limit?: number; alertType?: string; status?: string } = {}) => {
    const params = new URLSearchParams({ limit: String(filters.limit ?? 100) });
    if (filters.alertType) params.set('alertType', filters.alertType);
    if (filters.status) params.set('status', filters.status);
    return request<AlertLogEntry[]>(`/log?${params.toString()}`);
  },
};

export default adminAlertsService;
